'use client'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    useSyncExternalStore,
    type ReactNode,
} from 'react'

import { useDatadog } from '@/hooks/use-datadog'
import { useLogger } from '@/hooks/use-logger'
import { useWapi } from '@/hooks/use-wapi'
import { type SessionContext } from '@/services/auth'
import { performLogout } from '@/services/auth/client/logout'
import {
    clearMirror,
    getServerSessionSnapshot,
    getSessionSnapshot,
    readMirrorToken,
    subscribeToMirror,
} from '@/services/auth/client/session-mirror'
import { setSnowplowUserId } from '@/services/snowplow'

interface Profile {
    /** The mirror address this name was fetched for; absent until one resolves. */
    identity?: string
    firstName?: string
    lastName?: string
}

interface SessionContextValue {
    session: SessionContext
    /** Log out in place (client-direct): WAPI logout + clear mirror + clear analytics identity. */
    logout: () => Promise<void>
}

const SessionStateContext = createContext<SessionContextValue | null>(null)

/**
 * Provides client session state to the React tree. The session is read from the
 * localStorage mirror via `useSyncExternalStore`: the **server snapshot is always
 * logged-out** (so SSR/SSG HTML stays session-blind and cached pages remain
 * byte-identical), and the client snapshot resolves the real state after
 * hydration. Cross-tab mirror changes update it live. Sits beside `ClientEnvProvider`.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
    const wapi = useWapi()
    const datadog = useDatadog()
    const logger = useLogger({ module: 'session-provider' })

    const mirrorSession = useSyncExternalStore(
        subscribeToMirror,
        getSessionSnapshot,
        getServerSessionSnapshot,
    )

    // The name is not in the mirror — fetch it from userinfo.json post-hydration so the
    // navbar can greet by name. Best-effort: a failed fetch leaves no name, and the
    // navbar falls back to the generic logged-in label (never logged-out).
    //
    // It carries the address it was fetched for, because one document can serve two
    // people: a logout followed by a One-Tap sign-in swaps the mirror without
    // unmounting anything, and a fetch that fails for the second of them never
    // replaces the first one's name.
    const [profile, setProfile] = useState<Profile>({})
    const identity = mirrorSession.isAuthenticated ? mirrorSession.email : undefined

    useEffect(() => {
        if (!mirrorSession.isAuthenticated) {
            return
        }
        const token = readMirrorToken()
        if (!token) {
            return
        }
        let cancelled = false
        void wapi.getUserInfo(token).then((outcome) => {
            if (outcome.isErr) {
                // best-effort: greeting falls back to the generic label, but log so a
                // systematic userinfo failure is visible (low-cardinality metadata only).
                logger
                    .withMetadata({
                        code: outcome.error.code,
                        ...(outcome.error.statusCode ? { status: outcome.error.statusCode } : {}),
                    })
                    .debug('auth: userinfo greeting fetch failed')
                return
            }
            if (!cancelled) {
                setProfile({
                    identity,
                    firstName: outcome.value.firstName,
                    lastName: outcome.value.lastName,
                })
            }
        })
        return () => {
            cancelled = true
        }
    }, [identity, mirrorSession.isAuthenticated, wapi, logger])

    // A name is surfaced only while authenticated and only for the address it was
    // fetched against, so the previous visitor's name is never read as this one's —
    // by the greeting, or by anything downstream that reports an identity.
    const session: SessionContext = useMemo(() => {
        if (!mirrorSession.isAuthenticated) {
            return mirrorSession
        }
        const named = identity !== undefined && profile.identity === identity ? profile : {}
        return {
            ...mirrorSession,
            ...(named.firstName ? { firstName: named.firstName } : {}),
            ...(named.lastName ? { lastName: named.lastName } : {}),
        }
    }, [mirrorSession, identity, profile])

    const logout = useCallback(async () => {
        await performLogout({
            token: readMirrorToken(),
            logoutRequest: (token) => wapi.logout(token),
            clearMirror,
            setSnowplowUserId,
            clearRumUser: () => {
                datadog.clearUser()
            },
        })
    }, [wapi, datadog])

    return (
        <SessionStateContext.Provider value={{ session, logout }}>
            {children}
        </SessionStateContext.Provider>
    )
}

export function useSession(): SessionContextValue {
    const context = useContext(SessionStateContext)
    if (!context) {
        throw new Error('useSession must be used within a SessionProvider')
    }
    return context
}
